import {
  CompanyFactCategory,
  CompanyFactKey,
  ConflictStrategy,
  type FactCategoryConfig,
  type FactKeyConfig,
} from './core'
import { ARRIVAL_GUIDELINES_KEY_CONFIG } from './categories/arrival-guidelines'
import { BILLING_DETAILS_KEY_CONFIG } from './categories/billing-details'
import { BUSINESS_PROFILE_KEY_CONFIG } from './categories/business-profile'
import { CONTACTS_KEY_CONFIG } from './categories/contacts'
import { CUSTOMER_RULES_KEY_CONFIG } from './categories/customer-rules'
import {
  BACKFILLING_KEY_CONFIG,
  FORWARD_FILLING_KEY_CONFIG,
} from './categories/filling'
import { ONBOARDING_KEY_CONFIG } from './categories/onboarding'
import { PRICING_DETAILS_KEY_CONFIG } from './categories/pricing-details'
import { TIMEKEEPING_KEY_CONFIG } from './categories/timekeeping'
import { ROLE_SPECIFIC_INFO_KEY_CONFIG } from './categories/role-specific-info'
import { WORK_POLICIES_KEY_CONFIG } from './categories/work-policies'

// Derived keys that are custom-rendered by their own sections have no entry
// here — ROLE_TITLE / ROLE_PAY_RATE / ROLE_ATTRIBUTE (RoleSpecificFactsSection)
// and FACILITY_ADDRESS (FacilityAddressSection).
export const COMPANY_FACT_KEY_CONFIG: Partial<
  Record<CompanyFactKey, FactKeyConfig>
> = {
  ...BUSINESS_PROFILE_KEY_CONFIG,
  ...PRICING_DETAILS_KEY_CONFIG,
  ...TIMEKEEPING_KEY_CONFIG,
  ...BILLING_DETAILS_KEY_CONFIG,
  ...CONTACTS_KEY_CONFIG,
  ...WORK_POLICIES_KEY_CONFIG,
  ...ONBOARDING_KEY_CONFIG,
  ...ARRIVAL_GUIDELINES_KEY_CONFIG,
  ...FORWARD_FILLING_KEY_CONFIG,
  ...BACKFILLING_KEY_CONFIG,
  ...ROLE_SPECIFIC_INFO_KEY_CONFIG,
  ...CUSTOMER_RULES_KEY_CONFIG,
}

export const COMPANY_FACT_CATEGORY_CONFIG: Record<
  CompanyFactCategory,
  FactCategoryConfig
> = {
  [CompanyFactCategory.BUSINESS_PROFILE]: {
    displayName: 'Business Profile',
    keys: [
      CompanyFactKey.BUSINESS_PROFILE_WORKER_CLASSIFICATION,
      CompanyFactKey.BUSINESS_PROFILE_INDUSTRY,
      CompanyFactKey.BUSINESS_PROFILE_PAIN_POINTS,
      CompanyFactKey.BUSINESS_PROFILE_SUCCESS_METRICS,
      CompanyFactKey.BUSINESS_PROFILE_STAFFING_MODEL,
      CompanyFactKey.BUSINESS_PROFILE_ONSITE_STRATEGY,
      CompanyFactKey.BUSINESS_PROFILE_FACILITY_CONDITIONS,
      CompanyFactKey.BUSINESS_PROFILE_CULTURAL_ENVIRONMENT,
      CompanyFactKey.BUSINESS_PROFILE_DEMOGRAPHICS,
      CompanyFactKey.BUSINESS_PROFILE_CUSTOMER_PRIORITIES,
      CompanyFactKey.BUSINESS_PROFILE_NOTES,
    ],
  },
  [CompanyFactCategory.PRICING_DETAILS]: {
    displayName: 'Pricing Details',
    keys: [
      CompanyFactKey.PRICING_DETAILS_MARKUP_PERCENTAGE,
      CompanyFactKey.PRICING_DETAILS_MARKUP_TYPE,
      CompanyFactKey.PRICING_DETAILS_CONVERSION_FEE,
      CompanyFactKey.PRICING_DETAILS_NOTES,
    ],
  },
  [CompanyFactCategory.TIMEKEEPING]: {
    displayName: 'Timekeeping',
    keys: [
      CompanyFactKey.TIMEKEEPING_CLOCK_IN_METHOD,
      CompanyFactKey.TIMEKEEPING_TIMESHEET_EMAIL,
      CompanyFactKey.TIMEKEEPING_TIMESHEET_SCHEDULE,
      CompanyFactKey.TIMEKEEPING_TIME_DISPUTE_PROCESS,
      CompanyFactKey.TIMEKEEPING_NOTES,
    ],
  },
  [CompanyFactCategory.BILLING_DETAILS]: {
    displayName: 'Billing Details',
    keys: [
      CompanyFactKey.BILLING_DETAILS_BILLING_EMAIL,
      CompanyFactKey.BILLING_DETAILS_NET_TERMS,
      CompanyFactKey.BILLING_DETAILS_INVOICE_FREQUENCY,
      CompanyFactKey.BILLING_DETAILS_WORK_WEEK_END,
      CompanyFactKey.BILLING_DETAILS_PAY_SCHEDULE,
      CompanyFactKey.BILLING_DETAILS_OVERTIME_PAY,
      CompanyFactKey.BILLING_DETAILS_LATE_FEE_POLICY,
      CompanyFactKey.BILLING_DETAILS_INVOICE_DELIVERY_METHOD,
      CompanyFactKey.BILLING_DETAILS_INVOICE_CC_EMAILS,
      CompanyFactKey.BILLING_DETAILS_INVOICE_REQUIREMENTS,
      CompanyFactKey.BILLING_DETAILS_NOTES,
    ],
  },
  [CompanyFactCategory.INTERNAL_CONTACTS]: {
    displayName: 'Internal Contacts',
    keys: [
      CompanyFactKey.INTERNAL_CONTACTS_COPS,
      CompanyFactKey.INTERNAL_CONTACTS_WOPS,
    ],
  },
  [CompanyFactCategory.EXTERNAL_CONTACTS]: {
    displayName: 'External Contacts',
    keys: [CompanyFactKey.EXTERNAL_CONTACTS],
  },
  [CompanyFactCategory.WORK_POLICIES]: {
    displayName: 'Worker Expectations',
    keys: [
      CompanyFactKey.WORK_POLICIES_ATTIRE_POLICY,
      CompanyFactKey.WORK_POLICIES_ATTENDANCE_POLICY,
      CompanyFactKey.WORK_POLICIES_CALL_OUT_POLICY,
      CompanyFactKey.WORK_POLICIES_RETURN_POLICY,
      CompanyFactKey.WORK_POLICIES_HOLIDAYS,
      CompanyFactKey.WORK_POLICIES_COACHING_NOTES,
      CompanyFactKey.WORK_POLICIES_OT_EXPECTED,
      CompanyFactKey.WORK_POLICIES_OT_VOLUNTARY_MANDATORY,
      CompanyFactKey.WORK_POLICIES_NOTES,
    ],
  },
  [CompanyFactCategory.ONBOARDING]: {
    displayName: 'Onboarding',
    keys: [
      CompanyFactKey.ONBOARDING_BACKGROUND_CHECK,
      CompanyFactKey.ONBOARDING_DRUG_TEST,
      CompanyFactKey.ONBOARDING_RESUME_REVIEW,
      CompanyFactKey.ONBOARDING_INTERVIEW,
      CompanyFactKey.ONBOARDING_ONLINE_ORIENTATION,
      CompanyFactKey.ONBOARDING_SITE_ORIENTATION,
      CompanyFactKey.ONBOARDING_TOUR,
      CompanyFactKey.ONBOARDING_NOTES,
      CompanyFactKey.ONBOARDING_PROCESS_OVERVIEW,
      CompanyFactKey.ONBOARDING_ADDITIONAL_REQUIREMENTS,
    ],
  },
  [CompanyFactCategory.ARRIVAL_GUIDELINES]: {
    displayName: 'Arrival Guidelines',
    keys: [
      CompanyFactKey.ARRIVAL_GUIDELINES_PARKING,
      CompanyFactKey.ARRIVAL_GUIDELINES_ENTRANCE,
      CompanyFactKey.ARRIVAL_GUIDELINES_CONTACT_SUPERVISOR,
      CompanyFactKey.ARRIVAL_GUIDELINES_FIRST_DAY,
      CompanyFactKey.ARRIVAL_GUIDELINES_NOTES,
    ],
  },
  [CompanyFactCategory.FORWARD_FILLING]: {
    displayName: 'Forward Filling',
    keys: [
      CompanyFactKey.FORWARD_FILLING_OVERBOOKS,
      CompanyFactKey.FORWARD_FILLING_UNAPPROVED,
      CompanyFactKey.FORWARD_FILLING_NOTES,
    ],
  },
  [CompanyFactCategory.BACKFILLING]: {
    displayName: 'Backfilling',
    keys: [
      CompanyFactKey.BACKFILLING_BACKFILLABLE,
      CompanyFactKey.BACKFILLING_TIME_LIMIT,
      CompanyFactKey.BACKFILLING_PREFERENCES,
      CompanyFactKey.BACKFILLING_TIER_REQUIREMENT,
      CompanyFactKey.BACKFILLING_NOTES,
    ],
  },
  [CompanyFactCategory.ROLE_SPECIFIC_INFO]: {
    displayName: 'Role-specific info',
    keys: [
      CompanyFactKey.ROLE_TITLE,
      CompanyFactKey.ROLE_PAY_RATE,
      CompanyFactKey.ROLE_ATTRIBUTE,
      CompanyFactKey.ROLE_SCHEDULE_AND_PAY,
      CompanyFactKey.ROLE_PHYSICAL_REQUIREMENTS,
      CompanyFactKey.ROLE_SOFT_SKILLS,
      CompanyFactKey.ROLE_EQUIPMENT,
      CompanyFactKey.ROLE_LANGUAGE,
      CompanyFactKey.ROLE_GENDER_PREFERENCE,
      CompanyFactKey.ROLE_DISCOVERY_NOTES,
    ],
  },
  [CompanyFactCategory.FACILITY_INFO]: {
    displayName: 'Facility info',
    keys: [CompanyFactKey.FACILITY_ADDRESS],
  },
  [CompanyFactCategory.CUSTOMER_RULES]: {
    displayName: 'Customer Rules',
    keys: [
      CompanyFactKey.CUSTOMER_RULES_BUSINESS_SUPPORT,
      CompanyFactKey.CUSTOMER_RULES_WORKER_SUPPORT,
    ],
  },
}

export function getConflictStrategy(factKey: CompanyFactKey): ConflictStrategy {
  return (
    COMPANY_FACT_KEY_CONFIG[factKey]?.conflictStrategy ??
    ConflictStrategy.REPLACE
  )
}
